import { forwardRef, useEffect, useState, type HTMLAttributes, type ReactNode } from 'react'

export interface FadeProps extends HTMLAttributes<HTMLDivElement> {
  in?: boolean
  /** Transition duration in ms. @default 225 */
  timeout?: number
  children?: ReactNode
  onEnter?: () => void
  onExited?: () => void
}

/**
 * CSS opacity fade transition.
 * Drop-in replacement for MUI's Fade component.
 */
export const Fade = forwardRef<HTMLDivElement, FadeProps>(
  ({ in: show = true, timeout = 225, children, onEnter, onExited, style, ...rest }, ref) => {
    const [visible, setVisible] = useState(show)

    useEffect(() => {
      if (show) {
        setVisible(true)
        onEnter?.()
      } else {
        const timer = setTimeout(() => {
          setVisible(false)
          onExited?.()
        }, timeout)
        return () => clearTimeout(timer)
      }
    }, [show, timeout, onEnter, onExited])

    if (!visible && !show) {
      return null
    }

    return (
      <div
        ref={ref}
        style={{
          opacity: show ? 1 : 0,
          transition: `opacity ${timeout}ms ease-in-out`,
          ...style,
        }}
        {...rest}
      >
        {children}
      </div>
    )
  }
)

Fade.displayName = 'Fade'
